<template>
  <v-container>
    <v-row>
      <v-col>
        <h2 class="mb-4">Announcement Banners</h2>

        <!-- Create form -->
        <v-card class="mb-6 pa-4">
          <div class="text-subtitle-1 font-weight-bold mb-3">{{ editing ? 'Edit Banner' : 'New Banner' }}</div>
          <v-textarea v-model="form.message" label="Message" outlined dense rows="2" class="mb-2"></v-textarea>
          <v-select
            v-model="form.color"
            :items="colorOptions"
            label="Color"
            outlined dense class="mb-2"
          ></v-select>
          <v-select
            v-model="form.audience"
            :items="audienceOptions"
            label="Audience"
            outlined dense class="mb-2"
          ></v-select>
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="form.starts_at" label="Starts at" type="datetime-local" outlined dense></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="form.ends_at" label="Ends at" type="datetime-local" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <div class="d-flex gap-2 mt-2">
            <v-btn color="primary" :loading="saving" @click="save">
              {{ editing ? 'Save Changes' : 'Create Banner' }}
            </v-btn>
            <v-btn v-if="editing" text @click="cancelEdit">Cancel</v-btn>
          </div>
        </v-card>

        <!-- Banner list -->
        <v-card>
          <v-list>
            <div v-if="banners.length === 0" class="pa-4 grey--text text-center">No banners yet.</div>
            <v-list-item v-for="banner in banners" :key="banner.id" class="py-2">
              <v-list-item-content>
                <v-list-item-title>
                  <v-chip x-small :color="banner.color" dark class="mr-2">{{ banner.color }}</v-chip>
                  <v-chip x-small :color="banner.active ? 'success' : 'grey'" dark class="mr-2">
                    {{ banner.active ? 'Active' : 'Inactive' }}
                  </v-chip>
                  <v-chip x-small outlined class="mr-2">{{ audienceLabel(banner.audience) }}</v-chip>
                  {{ banner.message }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  {{ formatDate(banner.starts_at) }} → {{ formatDate(banner.ends_at) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action style="flex-direction: row; gap: 4px;">
                <v-btn icon small @click="startEdit(banner)">
                  <v-icon small>fal fa-pen</v-icon>
                </v-btn>
                <v-btn icon small @click="toggleActive(banner)">
                  <v-icon small>{{ banner.active ? 'fal fa-eye-slash' : 'fal fa-eye' }}</v-icon>
                </v-btn>
                <v-btn icon small @click="remove(banner)">
                  <v-icon small color="error">fal fa-trash</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import dayjs from "dayjs";

export default {
  data: () => ({
    banners: [],
    saving: false,
    editing: null,
    form: { message: "", color: "warning", audience: "all", starts_at: "", ends_at: "" },
    colorOptions: ["warning", "info", "error", "success"],
    audienceOptions: [
      { text: "Everyone",    value: "all" },
      { text: "Influencers", value: "influencers" },
      { text: "Businesses",  value: "businesses" },
      { text: "Moderators",  value: "moderators" },
    ],
  }),

  created() {
    this.load();
  },

  methods: {
    load() {
      this.$gql.query("announcementBanners", {
        result: "{ id message color audience starts_at ends_at active }",
      }).then(r => {
        this.banners = r.data.announcementBanners || [];
      });
    },

    save() {
      this.saving = true;
      if (this.editing) {
        this.$gql.mutate("updateAnnouncementBanner", {
          params: { id: this.editing, ...this.form },
          result: "{ id message color audience starts_at ends_at active }",
        }, true).then(() => { this.load(); this.cancelEdit(); }).finally(() => { this.saving = false; });
      } else {
        this.$gql.mutate("createAnnouncementBanner", {
          params: this.form,
          result: "{ id message color audience starts_at ends_at active }",
        }, true).then(() => { this.load(); this.resetForm(); }).finally(() => { this.saving = false; });
      }
    },

    startEdit(banner) {
      this.editing = banner.id;
      this.form = {
        message:   banner.message,
        color:     banner.color,
        audience:  banner.audience,
        starts_at: dayjs(banner.starts_at).format("YYYY-MM-DDTHH:mm"),
        ends_at:   dayjs(banner.ends_at).format("YYYY-MM-DDTHH:mm"),
      };
    },

    audienceLabel(audience) {
      const option = this.audienceOptions.find(o => o.value === audience);
      return option ? option.text : audience;
    },

    cancelEdit() {
      this.editing = null;
      this.resetForm();
    },

    resetForm() {
      this.form = { message: "", color: "warning", audience: "all", starts_at: "", ends_at: "" };
    },

    toggleActive(banner) {
      this.$gql.mutate("updateAnnouncementBanner", {
        params: { id: banner.id, active: !banner.active },
        result: "{ id active }",
      }, true).then(() => this.load());
    },

    remove(banner) {
      if (!confirm("Delete this banner?")) return;
      this.$gql.mutate("deleteAnnouncementBanner", {
        params: { id: banner.id },
        result: "",
      }, true).then(() => this.load());
    },

    formatDate(d) {
      return dayjs(d).format("DD MMM YYYY HH:mm");
    },
  },
};
</script>